import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { InertiaPlugin } from 'gsap/InertiaPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(Draggable, InertiaPlugin, ScrollTrigger);

const clubOptions = {
  draggableInstance: null,
  scrollTriggerInstance: null,

  init: function () {
    this.handleResponsiveBehavior();
    this.initImageCrossfade();
    this.handleClubSummarySelection();
  },

  handleClubSummarySelection: function () {
    // Check if we're on the club checkout pages
    const isClubCheckout = window.location.pathname.includes('/checkout/club/product') || window.location.pathname.includes('/checkout/club/customer');

    if (!isClubCheckout) return;

    // Create a MutationObserver to watch for changes in the DOM
    const observer = new MutationObserver((mutations, obs) => {
      const clubTitle = document.querySelector('.c7-club-summary__title');
      if (!clubTitle) return;

      // Check if it's the Joy of Zinfandel Club
      if (clubTitle.textContent.trim() === 'Joy of Zinfandel Club') {
        const clubSelection = document.querySelector('.c7-club-summary__selection');
        if (clubSelection) {
          clubSelection.style.display = 'none';
          // Disconnect the observer once we've found and modified the element
          obs.disconnect();
        }
      }
    });

    // Start observing the document with the configured parameters
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  },

  enableDragToScroll: function () {
    const scrollContainer = document.querySelector('.club-selections');
    const clubNamesFixed = document.querySelector('.club-names-fixed'); // Reference to the fixed element

    if (!scrollContainer || !clubNamesFixed) return;

    this.draggableInstance = Draggable.create(scrollContainer, {
      type: 'x',
      edgeResistance: 0.75,
      dragResistance: 0.1,
      inertia: true,
      allowNativeTouchScrolling: true,
      bounds: document.querySelector('.club-options'),
      dragClickables: true,
      onDrag: () => {
        // Apply the transform during the drag
        gsap.set(clubNamesFixed, { x: this.draggableInstance.x });
      },
      onThrowUpdate: () => {
        // Apply the transform during the inertia phase
        gsap.set(clubNamesFixed, { x: this.draggableInstance.x });
      },
    })[0];
  },

  disableDragToScroll: function () {
    if (this.draggableInstance) {
      this.draggableInstance.kill();
      this.draggableInstance = null;
    }
  },

  initScrollTrigger: function () {
    const clubNamesFixed = document.querySelector('.club-names-fixed');
    if (!clubNamesFixed) return;

    gsap.set(clubNamesFixed, { y: '-100%' });

    // GSAP ScrollTrigger setup
    this.scrollTriggerInstance = gsap.to(clubNamesFixed, {
      y: 0, // Animate to its original position
      ease: 'none', // Customize the easing function as needed
      scrollTrigger: {
        trigger: '.club-options .row.club-names',
        start: 'bottom top', // This triggers the animation when the '.row.club-names' bottom passes the top of the viewport
        endTrigger: '.club-selections',
        end: '95% top',
        //  markers: true, // Useful for debugging
        onEnter: () => {
          gsap.to(clubNamesFixed, { y: 0, duration: 0.25 }); // Animate to view
        },
        onLeave: () => {
          gsap.to(clubNamesFixed, { y: '-100%', duration: 0.25 }); // Animate out of view
        },
        onEnterBack: () => {
          gsap.to(clubNamesFixed, { y: 0, duration: 0.25 }); // Animate to view when scrolling back
        },
        onLeaveBack: () => {
          gsap.to(clubNamesFixed, { y: '-100%', duration: 0.25 }); // Animate out of view when scrolling back
        },
      },
    });
  },

  disableScrollTrigger: function () {
    if (this.scrollTriggerInstance) {
      // ScrollTrigger's kill method is used to clean up the instance
      this.scrollTriggerInstance.scrollTrigger.kill();
      this.scrollTriggerInstance.kill();
      this.scrollTriggerInstance = null;
    }
  },

  handleResponsiveBehavior: function () {
    const checkAndApplyBehavior = () => {
      const viewportWidth = window.innerWidth;
      this.initScrollTrigger();

      if (viewportWidth < 1280) {
        // Optionally disable the scroll trigger for smaller viewports
        // this.disableScrollTrigger(); // If you decide to disable the ScrollTrigger for smaller screens.
        //   this.enableDragToScroll();
      } else {
        this.disableDragToScroll(); // Disable Draggable before enabling ScrollTrigger
        // No need to explicitly call initScrollTrigger() here if it's always called before this check.
      }

      // Refresh ScrollTrigger calculations after any potential changes.
      ScrollTrigger.refresh();

      setTimeout(() => {
        // clubOptions.setWrapperHeight();
      }, 500);
    };

    checkAndApplyBehavior(); // Apply correct behavior on initial load

    window.addEventListener('resize', checkAndApplyBehavior);
  },

  initImageCrossfade: function () {
    const clubImageWrappers = document.querySelectorAll('.club-image-wrapper');

    clubImageWrappers.forEach((wrapper) => {
      const clubImage = wrapper.querySelector('.club-image');
      const lifestyleImage = wrapper.querySelector('.club-lifestyle-image');

      if (clubImage && lifestyleImage) {
        gsap.set(lifestyleImage, { opacity: 0 });

        wrapper.addEventListener('mouseenter', () => {
          gsap.to(clubImage, { opacity: 0, duration: 0.75, ease: 'power1.out' });
          gsap.to(lifestyleImage, { opacity: 1, duration: 0.75, ease: 'power1.out' });
        });

        wrapper.addEventListener('mouseleave', () => {
          gsap.to(clubImage, { opacity: 1, duration: 0.75, ease: 'power1.out' });
          gsap.to(lifestyleImage, { opacity: 0, duration: 0.75, ease: 'power1.out' });
        });
      }
    });
  },

  setWrapperHeight: function () {
    const wrapper = document.querySelector('.benefits-inner-wrapper');
    if (!wrapper) return;

    const calculateAndSetHeight = () => {
      // Ensure wrapper is visible and content is loaded
      const height = wrapper.offsetHeight; // Get the current rendered height
      // Set the height explicitly
      gsap.set(wrapper, { height: height });
    };

    window.onload = calculateAndSetHeight; // Wait for all content to load before calculating

    // Add resize event listener to update on viewport change, debounce to limit calls during resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(calculateAndSetHeight, 300);
    });
  },
};

export default clubOptions;
